import { ItineraryEvent } from "@/types/itinerary"
import { TripRef } from "@/types/trip"

import { itineraryApi } from "./api/itinerary-api"
import { EMPTY_ITINERARY } from "./itinerary-slice"
import { AppDispatch, RootState } from "./redux-store"

/**
 * Updates multiple events in the RTK Query cache for the itinerary.
 * Optimized to check/create cache entry once before processing all events.
 *
 * When the cache entry doesn't exist yet (e.g., streaming events arrive before
 * getItinerary completes), we use upsertQueryData with the events already known.
 *
 * IMPORTANT: upsertQueryData is async while updateQueryData is a synchronous
 * method. So we can't just upsert an empty itinerary in the cache AND then
 * right away use updateQueryData to add the new events
 */
export function updateEventsInCache(
  dispatch: AppDispatch,
  tripRef: TripRef,
  events: ItineraryEvent[],
  getState?: () => RootState
) {
  // ensure cache entry exists once before all updates
  if (getState) {
    const existingData = itineraryApi.endpoints.getItinerary.select({
      tripRef,
    })(getState()).data
    if (!existingData) {
      // No cache entry — upsert with all events included directly
      void dispatch(
        itineraryApi.util.upsertQueryData(
          "getItinerary",
          { tripRef },
          {
            ...EMPTY_ITINERARY,
            events: [...events],
          }
        )
      )
      // Must return here to skip the calls to updateQueryData below.
      // See comment above
      return
    }
  }

  events.forEach((event) => {
    dispatch(
      itineraryApi.util.updateQueryData(
        "getItinerary",
        { tripRef },
        (draft) => {
          if (!draft) return
          const index = draft.events.findIndex((e) => e.id === event.id)
          if (index === -1) {
            // Add new event at the beginning
            draft.events.unshift(event)
          } else if (draft.events[index].lock_version < event.lock_version) {
            // Update existing event if lock_version is newer
            draft.events[index] = event
          }
        }
      )
    )
  })
}
